import { Button } from '@alifd/next';
import React, { useContext, useEffect, useState } from 'react';
import { RiChargingPileLine } from 'react-icons/ri';

import client from '@/lib/feathers';
import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';
import type { ChargingPileGroup } from '@/types/database/chargingPile/chargingPileGroup';
import type { MqttPileData } from '@/types/database/chargingPile/chargingPileMqttData';

import { MqttContext } from '../../../MqttContextProvider';
import { GroupContext } from '../../../PileCtxProvider';
import { pileInfos } from '../../../UI/UI';
import BAGroup from '../BAGroup/BAGroup';
import StatusLight from './StatusLight';
import {
  BasicIcon,
  BasicInfos,
  BasicSection,
  CardTitle,
  CardWrapper,
  CurrentStatus,
  ElectricStatus,
  InfoContent,
  InfoTitle,
  SectionTitle,
  StatusSection,
  UsageStatus,
} from './styles';

const PileCard: React.FC<{
  pile: ChargingPile;
}> = ({ pile }) => {
  const [pileData, setPileData] = useState<MqttPileData>({
    power: 0,
    voltage: 0,
    current: 0,
    energy: 0,
    isCharging: false,
    isVoltage: false,
    status: 'noSignal',
    chargingTime: 0,
  });
  const [circuitGroup, setCircuitGroup] = useState<ChargingPileGroup>();
  const [initialValues, setInitialValues] =
    useState<ChargingPileElectricityRate>();

  const { groupData, setChart, manualControl } = useContext(GroupContext);
  const { mqttData } = useContext(MqttContext);

  const handleShowChart = () => {
    setChart({
      show: true,
      type: 'chargingPile',
      id: pile._id,
      name: pile.householdNumber || '無戶號',
    });
  };

  useEffect(() => {
    if (groupData) {
      const group = groupData
        .filter((data) => data.type === 'circuit')
        .find((data) => data.children.some((child) => child === pile._id));
      if (group) {
        setCircuitGroup(group);
      }
    }
  }, [groupData, pile._id]);

  useEffect(() => {
    if (mqttData) {
      const newData = mqttData?.chargingPiles?.[pile?.chargingPileID];
      if (newData) {
        setPileData((prev) => ({
          ...prev,
          ...newData,
        }));
      }
    }
  }, [pile, mqttData]);

  useEffect(() => {
    const getPriceSettingInitialValues = async () => {
      try {
        const { data } = await client
          .service('chargingPileElectricityRate')
          .find({});
        setInitialValues(data[0] || {});
      } catch (e) {
        console.error(e);
      }
    };
    getPriceSettingInitialValues();
  }, [pile]);

  return (
    <CardWrapper>
      <CardTitle>{pile.householdNumber || ''}</CardTitle>
      <SectionTitle>基本資料</SectionTitle>
      <BasicSection>
        <BasicIcon>
          <RiChargingPileLine
            color={pileInfos[pileData?.status || 'noSignal'].color}
          />
        </BasicIcon>
        <BasicInfos>
          <InfoTitle>車位編號：</InfoTitle>
          <InfoContent>{pile.parkingNumber}</InfoContent>
          <InfoTitle>充電樁編號：</InfoTitle>
          <InfoContent>{pile.chargingPileID}</InfoContent>
          <InfoTitle>電路群組：</InfoTitle>
          <InfoContent>{circuitGroup?.name || '無資料'}</InfoContent>
        </BasicInfos>
      </BasicSection>
      <SectionTitle>即時狀態</SectionTitle>
      <StatusSection>
        <CurrentStatus>
          <InfoTitle>狀態：</InfoTitle>
          <StatusLight
            color={pileInfos[pileData?.status || 'noSignal'].color}
          />
          <InfoContent>
            {`${pileInfos[pileData?.status || 'noSignal'].text}${
              pileData.status === 'waiting'
                ? `（排隊順序${pileData?.waitingOrder || 0}）`
                : ''
            }`}
          </InfoContent>
        </CurrentStatus>
        <ElectricStatus>
          <div>
            <InfoTitle>電壓</InfoTitle>
            <InfoContent>{`${pileData.voltage}伏特`}</InfoContent>
          </div>
          <div>
            <InfoTitle>電流</InfoTitle>
            <InfoContent>{`${pileData.current}安培`}</InfoContent>
          </div>
          <div>
            <InfoTitle>功率</InfoTitle>
            <InfoContent>{`${pileData.power}瓩`}</InfoContent>
          </div>
        </ElectricStatus>
        <UsageStatus>
          <InfoTitle>累積用量：</InfoTitle>
          <InfoContent>{`${pileData.energy}度`}</InfoContent>
        </UsageStatus>
        {initialValues?.isSetTimeLimit && (
          <UsageStatus>
            <InfoTitle>累積時數：</InfoTitle>
            <InfoContent>{`${pileData.chargingTime}分鐘`}</InfoContent>
          </UsageStatus>
        )}

        {manualControl && (
          <BAGroup pile={pile} pileStatus={pileData.status || ''} />
        )}
        <Button onClick={handleShowChart}>查看用電圖表</Button>
      </StatusSection>
    </CardWrapper>
  );
};

export default React.memo(PileCard);
